import React, { useRef } from "react";
import styled from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import imgSrc from "../../assets/homeAssets/focusImg.jpg";

gsap.registerPlugin(ScrollTrigger);

const FocusDetailSection = () => {
  const container = useRef();
  const imageRef = useRef();
  const textRef = useRef();

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: "top top",
          end: "+=150%", // How long the zoom lasts
          scrub: 1,
          pin: true,
        },
      });

      tl.to(imageRef.current, {
        scale: 1.8, // Zoom in deep
        filter: "blur(8px)", // Create the depth of field effect
        ease: "none",
        opacity: 1.4,
      }).to(
        textRef.current,
        {
          opacity: 1,
          y: -20,
          ease: "power2.out",
        },
        "<50%",
      ); // Text starts fading in halfway through the zoom
    },
    { scope: container },
  );

  return (
    <SectionWrapper ref={container}>
      {/* THE IMAGE LAYER */}
      <ImageWrapper>
        <StyledImage ref={imageRef} src={imgSrc} alt="Skin Detail" />
        {/* VIGNETTE OVERLAY: Makes the center feel like a lens */}
        <Vignette />
      </ImageWrapper>

      {/* THE TEXT LAYER */}
      <Content ref={textRef}>
        <Subtitle>Präzision in jeder Pore</Subtitle>
        <Title>Der Fokus liegt im Detail</Title>
        <Description>
          Jede Behandlung wird individuell auf die Bedürfnisse deiner Haut
          abgestimmt und folgt höchsten professionellen Standards. Unser
          Anspruch ist es, Ästhetik nicht nur sichtbar, sondern spürbar zu
          machen.
        </Description>
      </Content>
    </SectionWrapper>
  );
};

export default FocusDetailSection;

// --- Styled Components ---

const SectionWrapper = styled.section`
  height: 100vh;
  width: 100%;
  /* background-color: #000; */
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.1); /* Start slightly zoomed for a smooth entry */
`;

const Vignette = styled.div`
  position: absolute;
  inset: 0;
  /* This creates a "camera lens" feel, darker at the edges */
  background: radial-gradient(
    circle,
    rgba(0, 0, 0, 0) 20%,
    rgba(236, 215, 215, 0.8) 100%
  );
`;

const Content = styled.div`
  position: relative;
  z-index: 10;
  text-align: center;
  max-width: 700px;
  color: #fff;
  padding: 2rem;
  opacity: 0; /* Hidden initially, fades in via GSAP */
  pointer-events: none;
`;

const Subtitle = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.4rem;
  font-size: 0.9rem;
  color: #eeeadf; /* Aesthetic Gold */
  display: block;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-family: "Playfair Display", serif;
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 400;
  line-height: 1.1;
  margin-bottom: 2rem;
`;

const Description = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.7);
  max-width: 500px;
  margin: 0 auto;
`;
